import { useEffect, useState } from 'react'

export const Drawer_MOTION_DURATION_MS = 240

// 各 panel 组件通过 useImperativeHandle 暴露的命令式句柄：业务组件持有它来打开面板。
export type DrawerHandle<P = void> = { open: (params: P) => void }

// 浮层开关状态机：管理单个 panel 的打开/关闭/完成（240ms 动画时序）。
// 由各 panel 组件自身持有，通过 useImperativeHandle 暴露 open()；业务组件只持 handle，不感知本 hook。
export const useDrawer = <P = void>() => {
  const [active, setActive] = useState<boolean>(false)
  const [entered, setEntered] = useState(false)
  const [closing, setClosing] = useState(false)
  const [params, setParams] = useState<P | null>(null)

  // entered：进入动画开关。
  // 原因：CSS transition 只在「已挂载并绘制过一帧的元素」属性发生变化时才触发。
  //   面板打开前 active=false 直接 return null（DOM 无此元素），open() 后若一出生就渲染在最终显示态，
  //   浏览器直接把它画在终点、没有起点可过渡 → 打开闪现；而关闭是从已绘制的显示态改到隐藏态，有变化 → 有动画。
  // 修法：先以隐藏态挂载（entered=false）绘制一帧，下一帧（requestAnimationFrame）再翻到显示态（entered=true），
  //   进入过渡才会跑起来。组件里据此让「未进入 或 正在关闭」都用隐藏态。
  useEffect(
    () => {
      if (!active) {
        return
      }
      const id = requestAnimationFrame(() => setEntered(true))
      return () => cancelAnimationFrame(id)
    },
    [active],
  )

  const open = (nextParams: P) => {
    console.log('useDrawer open', nextParams)
    setClosing(false)
    setEntered(false)
    setParams(nextParams)
    setActive(true)
  }

  const close = () => {
    if (!active || closing) {
      return
    }
    setClosing(true)
    window.setTimeout(
      () => {
        setActive(false)
        setParams(null)
        setClosing(false)
        setEntered(false)
      },
      Drawer_MOTION_DURATION_MS,
    )
  }

  const complete = (onComplete?: () => void) => {
    if (!active || closing) {
      return
    }
    setClosing(true)
    window.setTimeout(
      () => {
        setActive(false)
        setParams(null)
        setClosing(false)
        setEntered(false)
        onComplete?.()
      },
      Drawer_MOTION_DURATION_MS,
    )
  }

  return {
    active,
    entered,
    closing,
    params,
    open,
    close,
    complete,
  }
}
